import * as tslib_1 from "tslib";
import getConversationTailoredXpData from './getConversationTailoredXpData';
import { MAX_ITEMS_ALLOWED_TO_RETRIEVE } from '../actions/loadConversation';
import createEmptyConversationItemParts from '../utils/createEmptyConversationItemParts';
import preserveItemDataInCache from '../utils/preserveItemDataInCache';
import initializeUndefinedItemProperties, { itemMandatoryProperties, } from '../utils/initializeUndefinedItemProperties';
import { trace } from 'owa-trace';
import { action, select } from 'satcheljs/lib/legacy';
import { raiseAction } from 'satcheljs/lib/legacy/stitch';
import { conversationCache, mailStore, LocalLieState, } from 'owa-mail-store';
import tryRemoveFromMailStoreItems from 'owa-mail-actions/lib/triage/tryRemoveFromMailStoreItems';
var CHRONOLOGICAL_NEWEST_ON_BOTTOM_SORT_ORDER_NAME = 'ChronologicalNewestOnBottom';
var conversationNodeIdProvider = function (conversationNode) {
    return conversationNode.InternetMessageId;
};
var itemIdProvider = function (item) {
    return item.ItemId.Id;
};
/** Generate array of ids from array of objects. */
function generateIdsFromObjects(objects, idProvider) {
    var ids = [];
    objects.forEach(function (object) {
        ids.push(idProvider(object));
    });
    return ids;
}
/** Compare array of ids between old and new. */
function checkIfDifferentIds(oldIds, newIds) {
    if (oldIds.length != newIds.length) {
        return true;
    }
    for (var i = 0; i < oldIds.length; i++) {
        if (oldIds[i] != newIds[i]) {
            return true;
        }
    }
    return false;
}
/** Get the old ids that are not included in the array of new ids */
function getAdditionalOldIds(oldIds, newIds) {
    var additionalOldIds = [];
    oldIds.forEach(function (oldId) {
        if (newIds.indexOf(oldId) < 0) {
            additionalOldIds.push(oldId);
        }
    });
    return additionalOldIds;
}
/** Clear and refill with new ids if they are different from old ids. */
/** Also return the old ids that are not included in the new ids. */
function resetIdsIfDifferent(oldIds, objects, idProvider) {
    var newIds = generateIdsFromObjects(objects, idProvider);
    if (checkIfDifferentIds(oldIds, newIds)) {
        var additionalOldIds = getAdditionalOldIds(oldIds, newIds);
        // Clear and refill.
        var oldCount = oldIds.length;
        oldIds.splice.apply(oldIds, tslib_1.__spread([0, oldCount], newIds));
        return [true, additionalOldIds];
    }
    return [false, []];
}
function checkIfCollectionChanged(change) {
    return change.added.length > 0 || change.removed.length > 0 || change.modified.length > 0;
}
function checkIfItemMandatoryPropertiesCollectionChanged(change) {
    return change.isReadChangedToUnread.length > 0;
}
function cleanUpItem(itemId, itemsState) {
    tryRemoveFromMailStoreItems(itemId, 1 /* ConversationItemParts */);
}
function cleanUpConversationNode(nodeId, conversationNodesState, itemsState) {
    var nodeToRemove = conversationNodesState.get(nodeId);
    nodeToRemove.itemIds.forEach(function (itemId) {
        cleanUpItem(itemId, itemsState);
    });
    conversationNodesState.delete(nodeId);
}
function checkItemMandatoryPropertiesChange(nodeId, newItems, itemsState, nodeIdCollctionWithItemMandatoryPropertiesChanged) {
    var e_1, _a;
    try {
        for (var newItems_1 = tslib_1.__values(newItems), newItems_1_1 = newItems_1.next(); !newItems_1_1.done; newItems_1_1 = newItems_1.next()) {
            var newItem = newItems_1_1.value;
            var oldItem = itemsState.get(newItem.ItemId.Id);
            if (oldItem) {
                if (oldItem.IsRead && !newItem.IsRead) {
                    nodeIdCollctionWithItemMandatoryPropertiesChanged.isReadChangedToUnread.push(nodeId);
                }
            }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (newItems_1_1 && !newItems_1_1.done && (_a = newItems_1.return)) _a.call(newItems_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
}
/** @returns collection change of ids for conversation nodes. */
function mergeConversationNodes(oldIds, newConversationNodes, conversationNodesState, itemsState, conversationSortOrder, conversationId) {
    preservePendingNodesIfNecessary(oldIds, newConversationNodes, conversationNodesState, itemsState, conversationSortOrder);
    var additionalOldIds = resetIdsIfDifferent(oldIds, newConversationNodes, conversationNodeIdProvider)[1];
    var nodeIdCollectionChange = {
        added: [],
        removed: [],
        modified: [],
    };
    var nodeIdCollectionWithItemMandatoryPropertiesChanged = {
        isReadChangedToUnread: [],
    };
    // Merge each new node with the old.
    newConversationNodes.forEach(function (newNode) {
        var nodeId = conversationNodeIdProvider(newNode);
        var oldNode = conversationNodesState.get(nodeId);
        var nodeToMerge;
        if (!oldNode) {
            // This is the case where a new node is added.
            // Create and save a node to merge.
            conversationNodesState.set(nodeId, {
                conversationId: conversationId.Id,
                internetMessageId: nodeId,
                itemIds: [],
                quotedTextList: newNode.QuotedTextList,
                hasQuotedText: newNode.HasQuotedText,
                isQuotedTextChanged: newNode.IsQuotedTextChanged,
                quotedTextState: newNode.QuotedTextState,
                localLieState: LocalLieState.None,
                parentInternetMessageId: newNode.ParentInternetMessageId,
            });
            nodeToMerge = conversationNodesState.get(nodeId);
            nodeIdCollectionChange.added.push(nodeId);
        }
        else {
            // We need to merge the parent internet message id for the bus stops to be refreshed. This is because
            // the server will not send down the parent internet message id if the node is the last node in the GCI.
            if (newNode.ParentInternetMessageId) {
                oldNode.parentInternetMessageId = newNode.ParentInternetMessageId;
            }
            if (newNode.QuotedTextList) {
                oldNode.quotedTextList = newNode.QuotedTextList;
            }
            if (newNode.QuotedTextState) {
                oldNode.quotedTextState = newNode.QuotedTextState;
            }
            if (newNode.HasQuotedText) {
                oldNode.hasQuotedText = true;
            }
            if (newNode.IsQuotedTextChanged) {
                oldNode.isQuotedTextChanged = true;
            }
            nodeToMerge = oldNode;
        }
        var oldItemIds = nodeToMerge.itemIds;
        var _a = tslib_1.__read(resetIdsIfDifferent(oldItemIds, newNode.Items, itemIdProvider), 2), isItemArrayDifferent = _a[0], additionalOldItemIds = _a[1];
        if (oldNode && isItemArrayDifferent) {
            // This is the case where an old node is modified.
            nodeIdCollectionChange.modified.push(nodeId);
        }
        if (oldNode && !isItemArrayDifferent) {
            checkItemMandatoryPropertiesChange(nodeId, newNode.Items, itemsState, nodeIdCollectionWithItemMandatoryPropertiesChanged);
        }
        // Merge items for each new node.
        mergeItems(oldItemIds, newNode.Items, additionalOldItemIds, itemsState, conversationId.mailboxInfo);
    });
    // Clean up any additional old node.
    additionalOldIds.forEach(function (additionalOldId) {
        // This is the case where an old node is removed.
        cleanUpConversationNode(additionalOldId, conversationNodesState, itemsState);
        nodeIdCollectionChange.removed.push(additionalOldId);
    });
    return [nodeIdCollectionChange, nodeIdCollectionWithItemMandatoryPropertiesChanged];
}
/** @returns [whether item collection has been changed, whether item collection contains item changed from read to unread] */
function mergeItems(oldIds, newItems, additionalOldIds, itemsState, mailboxInfo) {
    // Merge each new item with the old.
    newItems.forEach(function (newItem) {
        var newItemId = newItem.ItemId.Id;
        var oldItem = itemsState.get(newItemId);
        // Initialize required properties that could be undefined so they're observable in the store.
        initializeUndefinedItemProperties(newItem);
        if (!newItem.ContainsOnlyMandatoryProperties) {
            // Only cache new item from response if new item contains complete list of properties.
            // Sync state should guarantee that if we already have an item, the GCI response only contains mandatory properties.
            // But some scenarios (TxP) ignore the sync state, so we should preserve any item data we may have fetched so it doesn't get overwritten.
            if (oldItem) {
                preserveItemDataInCache(newItem, oldItem);
            }
            itemsState.set(newItemId, tslib_1.__assign({}, newItem, { MailboxInfo: mailboxInfo, SmartReplyData: null, SmartTimeData: null, AttachmentSuggestions: null, smartPillFeedbackSubmitted: false }));
        }
        else {
            if (oldItem) {
                // Update mandatory properties on old item if it exists in cache.
                itemMandatoryProperties.forEach(function (property) {
                    if (typeof newItem[property.name] !== 'undefined') {
                        oldItem[property.name] = newItem[property.name];
                    }
                });
            }
            else {
                // Remove item id from ConversationNode.ItemIds if the old item isn't cached and new item only contains mandatory properties from response.
                oldIds.splice(oldIds.indexOf(newItemId), 1);
            }
        }
    });
    // Clean up any additional old item.
    additionalOldIds.forEach(function (additionalOldId) {
        cleanUpItem(additionalOldId, itemsState);
    });
}
function handleNodesWithDuplicateIds(conversationNodesState, conversationNodes, conversationSortOrder, conversationId) {
    var nodeIdMap = {};
    var nodeCount = conversationNodes.length;
    var isNewestOnBottom = conversationSortOrder == CHRONOLOGICAL_NEWEST_ON_BOTTOM_SORT_ORDER_NAME;
    var index = isNewestOnBottom ? 0 : nodeCount - 1;
    var increment = isNewestOnBottom ? 1 : -1;
    var _loop_1 = function () {
        var node = conversationNodes[index];
        var existingNode = conversationNodesState.get(node.InternetMessageId);
        if ((existingNode && existingNode.conversationId != conversationId.Id) ||
            nodeIdMap[node.InternetMessageId]) {
            // If there's node with the same InternetMessageId from a different conversation or
            // there's a node with the same InternetMessageId in the same conversation,
            // then append ItemId to InternetMessageId to make it unique
            node.InternetMessageId += node.Items[0].ItemId.Id;
            // Update InternetMessageId of each item in the node
            node.Items.forEach(function (item) { return (item.InternetMessageId = node.InternetMessageId); });
        }
        else {
            nodeIdMap[node.InternetMessageId] = true;
        }
        index += increment;
    };
    while (index >= 0 && index < nodeCount) {
        _loop_1();
    }
}
function preservePendingNodesIfNecessary(oldIds, newConversationNodes, conversationNodesState, itemsState, conversationSortOrder) {
    if (oldIds.length > 0) {
        // Check if last node is pending
        var isNewestOnBottom = conversationSortOrder == CHRONOLOGICAL_NEWEST_ON_BOTTOM_SORT_ORDER_NAME;
        var indexOfLastNode = isNewestOnBottom ? oldIds.length - 1 : 0;
        var lastNode = conversationNodesState.get(oldIds[indexOfLastNode]);
        if (lastNode.localLieState == LocalLieState.Pending) {
            // If so, see if the response collection contains a node with a matching IMI
            var shouldPreserveLocalLie = true;
            var matchingNode = findNodeById(newConversationNodes, lastNode.internetMessageId);
            if (matchingNode) {
                if (matchingNode.Items[0].IsDraft) {
                    // If the matching node is a draft, remove it since we'll preserve the local lie node.
                    newConversationNodes.splice(newConversationNodes.indexOf(matchingNode), 1);
                }
                else {
                    // If the matching node is a normal item, keep it and update the local lie node.
                    shouldPreserveLocalLie = false;
                    updateLocalLieNode(lastNode, matchingNode);
                }
            }
            if (shouldPreserveLocalLie) {
                // Set the matching node to have the same IMI and itemId so the local lie node persists.
                matchingNode = {
                    InternetMessageId: lastNode.internetMessageId,
                    Items: [itemsState.get(lastNode.itemIds[0])],
                };
                if (isNewestOnBottom) {
                    newConversationNodes.push(matchingNode);
                }
                else {
                    newConversationNodes.unshift(matchingNode);
                }
            }
        }
    }
}
function updateLocalLieNode(localLieNode, matchingNode) {
    localLieNode.localLieState = LocalLieState.Complete;
    localLieNode.hasQuotedText = matchingNode.HasQuotedText;
    localLieNode.isQuotedTextChanged = matchingNode.IsQuotedTextChanged;
    localLieNode.quotedTextList = matchingNode.QuotedTextList;
}
function findNodeById(conversationNodes, nodeId) {
    var e_2, _a;
    try {
        for (var conversationNodes_1 = tslib_1.__values(conversationNodes), conversationNodes_1_1 = conversationNodes_1.next(); !conversationNodes_1_1.done; conversationNodes_1_1 = conversationNodes_1.next()) {
            var conversationNode = conversationNodes_1_1.value;
            if (conversationNodeIdProvider(conversationNode) == nodeId) {
                return conversationNode;
            }
        }
    }
    catch (e_2_1) { e_2 = { error: e_2_1 }; }
    finally {
        try {
            if (conversationNodes_1_1 && !conversationNodes_1_1.done && (_a = conversationNodes_1.return)) _a.call(conversationNodes_1);
        }
        finally { if (e_2) throw e_2.error; }
    }
    return null;
}
export var ConversationReadyToLoadStitchName = 'conversationReadyToLoadStitch';
export var UpdateItemMandatoryPropertiesStitchActionName = 'updateItemMandatoryPropertiesStitchAction';
export default action('parseConversationResponse')(select({
    conversations: function () { return conversationCache; },
    conversationNodes: function () { return mailStore.conversationNodes; },
    items: function () { return mailStore.items; },
})(function parseConversationResponse(responseMessage, conversationSortOrder, conversationId, state) {
    var conversationRawId = conversationId.Id;
    var conversationToLoad = state.conversations.get(conversationRawId);
    if (!conversationToLoad) {
        // If conversation is not in cache, it must be purged. In this case, we need to create an empty one.
        conversationToLoad = createEmptyConversationItemParts(conversationId, conversationSortOrder);
    }
    // Save empty conversationItemPart or touch conversationCache
    state.conversations.add(conversationRawId, conversationToLoad);
    // Property hasLoadFailed is undefined implies we create an empty ConversationItemParts due to no cache or previous cache failed,
    // update hasLoadFailed if it's undefined, otherwise, keep the cached value as is.
    var hasLoadFailed = !(responseMessage && responseMessage.ResponseClass == 'Success');
    if (conversationToLoad.loadingState.hasLoadFailed === undefined) {
        conversationToLoad.loadingState.hasLoadFailed = hasLoadFailed;
    }
    if (conversationToLoad.loadingState.hasLoadFailed) {
        // Notify reading pane that load has failed.
        raiseAction(ConversationReadyToLoadStitchName)(conversationRawId);
    }
    // Parse Success conversation response
    if (!hasLoadFailed) {
        var conversationResponse = responseMessage.Conversation;
        if (!conversationResponse.ConversationNodes) {
            trace.warn("GCI response has no ConversationNodes, but TotalConversationNodesCount is " + conversationResponse.TotalConversationNodesCount);
            // Even though the response was successful, we don't have anything to show.
            // Set hasLoadFailed and notify reading pane so we show the error message.
            conversationToLoad.loadingState.hasLoadFailed = true;
            raiseAction(ConversationReadyToLoadStitchName)(conversationRawId);
            return;
        }
        // Only update SyncState when the conversation node counts match.
        // When send mail to self, it is possible that the conversation table is updated according to sent item, but the message is not delivered yet.
        // In this case the syncState might contain new message id but GCI response doesn't really contain this conversationNode.
        if (conversationResponse.ConversationNodes.length ==
            conversationResponse.TotalConversationNodesCount) {
            conversationToLoad.syncState = conversationResponse.SyncState;
        }
        else {
            var nodeCount = conversationResponse.ConversationNodes.length;
            var totalNodeCount = conversationResponse.TotalConversationNodesCount;
            trace.warn("Count of conversationNodes (" + nodeCount + ") returned from GetConversationItems doesn't match TotalConversationNodesCount (" + totalNodeCount + ")");
        }
        // Update load more value
        conversationToLoad.canLoadMore =
            conversationToLoad.maxItemsToReturn < MAX_ITEMS_ALLOWED_TO_RETRIEVE &&
                conversationResponse.ConversationNodes &&
                !(conversationResponse.ConversationNodes.length <
                    conversationToLoad.maxItemsToReturn);
        conversationToLoad.isLoadMoreInProgress = false;
        conversationToLoad.canDelete = conversationResponse.CanDelete || null;
        // Handle nodes with duplicate ids.
        // Append ItemId to InternetMessageId to make it unique whenever a duplicate InternetMessageId is detected.
        // This usually happens to system-generated emails wrongfully using the same InternetMessageId.
        // Note that it doesn't handle the case where the first node/conversation with duplicate InternetMessageId is hard deleted,
        // but it's a corner case and there's no other way to correlate.
        handleNodesWithDuplicateIds(state.conversationNodes, conversationResponse.ConversationNodes, conversationSortOrder, conversationId);
        // Fetch TxP data if necessary
        getConversationTailoredXpData(conversationResponse.ConversationNodes, conversationSortOrder);
        // Merge nodes.
        var _a = tslib_1.__read(mergeConversationNodes(conversationToLoad.conversationNodeIds, conversationResponse.ConversationNodes, state.conversationNodes, state.items, conversationSortOrder, conversationId), 2), nodeIdCollectionChanged = _a[0], nodeIdCollectionWithItemMandatoryPropertiesChanged = _a[1];
        // Notify reading pane to update readingPaneStore if there's collection change.
        if (checkIfCollectionChanged(nodeIdCollectionChanged)) {
            raiseAction(ConversationReadyToLoadStitchName)(conversationRawId, nodeIdCollectionChanged);
        }
        // If has item changed to unread from notification, notify reading pane to expand the item part.
        // If has item changed to read from notification, leave the item part as is.
        if (checkIfItemMandatoryPropertiesCollectionChanged(nodeIdCollectionWithItemMandatoryPropertiesChanged)) {
            raiseAction(UpdateItemMandatoryPropertiesStitchActionName)(conversationRawId, nodeIdCollectionWithItemMandatoryPropertiesChanged);
        }
    }
}));
//# sourceMappingURL=parseConversationResponse.js.map